// Catálogo de artículos de ayuda del panel (GOL-11, capa 1 de D8).
//
// Vive en shared porque tiene DOS consumidores que no pueden divergir: la UI del
// panel (que los busca y los muestra) y el asistente de ayuda del worker (que los
// usa como su única fuente para responder). Si el asistente se alimentara de otro
// texto, terminaría explicando un producto distinto al que documenta el FAQ.
//
// El catálogo es código versionado a propósito: son instrucciones sobre cómo
// funciona el panel, así que cambian en el mismo commit que cambia el panel.

export type HelpCategoryId = 'empezar' | 'sitio' | 'contenido' | 'clientes' | 'herramientas' | 'cuenta';

export interface HelpCategory {
  id: HelpCategoryId;
  label: string;
  description: string;
}

// El orden importa: `empezar` va primero porque el índice del centro de ayuda lo
// usa tal cual, y quien llega perdido necesita esa categoría antes que ninguna.
export const HELP_CATEGORIES: HelpCategory[] = [
  { id: 'empezar', label: 'Primeros pasos', description: 'Qué hacer primero y qué es cada cosa del panel.' },
  { id: 'sitio', label: 'Mi sitio y publicación', description: 'Publicar, dominio, apariencia y datos de contacto.' },
  { id: 'contenido', label: 'Contenido y secciones', description: 'Textos, imágenes, páginas y secciones.' },
  { id: 'clientes', label: 'Clientes y mensajes', description: 'Quién te escribe, quién te visita y dónde lo ves.' },
  { id: 'herramientas', label: 'Reservas, encuestas y reseñas', description: 'Las herramientas que trabajan por ti.' },
  { id: 'cuenta', label: 'Mi cuenta y mi pago', description: 'Plan, recibos, accesos y equipo.' },
];

export interface HelpArticle {
  id: string;
  category: HelpCategoryId;
  question: string;
  /** Párrafos en texto plano — sin markdown, para no arrastrar un renderer. */
  body: string[];
  /** Pantalla del panel donde se hace la tarea. La ayuda termina en la acción. */
  to?: string;
  toLabel?: string;
  /** Términos con los que un cliente buscaría esto, incluidos los coloquiales. */
  keywords: string[];
  /** Se listan como "lo más buscado" en el índice del drawer. */
  featured?: boolean;
}

export const HELP_ARTICLES: HelpArticle[] = [
  {
    id: 'herramientas-calculadora', category: 'herramientas',
    question: '¿Cómo añado y pruebo una calculadora en mi sitio?',
    body: [
      'En Herramientas e integraciones, Explorar muestra únicamente las opciones disponibles para tu negocio. Añadir una herramienta no publica nada.',
      'Configura sus textos y plazo sugerido. La tasa empieza vacía; si propones una, indica su fuente, fecha y vigencia. El aviso informativo es obligatorio y el contacto usa los canales de tu sitio.',
      'Guardar borrador conserva tus ajustes sin cambiar la configuración válida. Probar cálculo usa los datos que introduces sin almacenarlos. Restablecer valores reemplaza únicamente el borrador por los de la versión instalada; después debes guardar.',
      'Elige páginas e idiomas, guarda las ubicaciones y abre Vista previa guardada. Publicar cambios del sitio incluye todos los cambios guardados, también los de otras páginas. Solo la confirmación de publicación acredita qué configuración está en el sitio.',
      'Si desactivas, se conservan los ajustes y se detienen los cálculos. Desinstalar elimina ajustes y ubicaciones. Retirar el contenido estático requiere publicar otra vez. Una suspensión de plataforma también impide calcular.',
    ],
    to: '/tools', toLabel: 'Ver mis herramientas', keywords: ['calculadora', 'hipoteca', 'mensualidad', 'herramientas', 'plugin', 'borrador', 'tasa', 'restablecer'],
  },
  // ── Primeros pasos ────────────────────────────────────────────────────────
  {
    id: 'por-donde-empiezo',
    category: 'empezar',
    question: 'Acabo de entrar, ¿por dónde empiezo?',
    body: [
      'Tu sitio ya existe y ya se puede visitar: no empiezas de cero. Lo que sigue es dejarlo con la información que hace que alguien te elija.',
      'En Primeros pasos tienes la lista completa con lo que llevas y lo que falta. Algunas tareas las marcamos solos en cuanto las completas; otras las marcas tú.',
      'Si solo tienes diez minutos, este es el orden que más rinde: revisa que tu horario, tu teléfono y tu dirección estén bien; cambia la foto y el texto de la portada por algo tuyo; y publica para que el cambio se vea. Con eso ya puedes compartir tu sitio sin pena.',
      'En Inicio te vamos proponiendo la siguiente mejora según lo que le falta a tu contenido, así que no tienes que acordarte de todo.',
    ],
    to: '/getting-started',
    toLabel: 'Ver mis primeros pasos',
    keywords: ['empezar', 'primeros pasos', 'nuevo', 'no se que hacer', 'por donde', 'perdido', 'guia', 'tutorial'],
    featured: true,
  },
  {
    id: 'que-hay-en-el-menu',
    category: 'empezar',
    question: '¿Qué es cada cosa del menú?',
    body: [
      'Inicio es tu consola: el estado de tu negocio, lo que pasó últimamente y la caja donde le pides cambios a la inteligencia artificial.',
      'Sitio es donde vive tu página: sus páginas y textos, la apariencia, tus datos de contacto, la página de enlaces, los anuncios y los avisos legales.',
      'Clientes junta a la gente que te dejó sus datos, y Archivos e imágenes guarda tus fotos y documentos.',
      'Después vienen las herramientas que trabajan por ti: Reservas para las citas, Mis reseñas para las opiniones, Encuestas y campañas para captar interés, Asistente para el ayudante que contesta en tu sitio y Resumen y visibilidad para saber qué tan fácil es encontrarte.',
      'Hasta abajo está lo tuyo: Ajustes, Mi pago y esta Ayuda. Según tu plan puede que no veas todas las secciones.',
      'Todo lo que cambies aquí llega a tu sitio cuando publicas.',
    ],
    keywords: ['menu', 'secciones', 'para que sirve', 'que es cada', 'no entiendo', 'panel', 'donde esta'],
  },
  {
    id: 'pedir-cambio-ia',
    category: 'empezar',
    question: 'Pedir un cambio sin entrar al editor',
    body: [
      'En Inicio hay una caja donde escribes lo que quieres, como se lo dirías a alguien: «cambia mi horario del domingo», «publica un aviso de que cerramos el 15», «cambia el texto del inicio».',
      'Te decimos qué vamos a hacer y tú confirmas antes de que se aplique. Nada se hace a tus espaldas.',
      'Abajo de la caja hay atajos para lo que más se pide, y los que uses más seguido se te quedan a la mano.',
    ],
    to: '/',
    toLabel: 'Ir a Inicio',
    keywords: ['pedir', 'asistente', 'ia', 'inteligencia artificial', 'cambio rapido', 'sin editar', 'atajos'],
  },
  {
    id: 'retomar-alta',
    category: 'empezar',
    question: 'Dejé el alta a medias, ¿cómo la termino?',
    body: [
      'No perdiste nada: lo que ya habías elegido quedó guardado. Cuando dejas el alta en pausa, Inicio te recibe con un aviso para continuar donde te quedaste.',
      'Si prefieres seguir por tu cuenta, tu sitio ya funciona igual: lo que faltaba del alta también se puede llenar desde Sitio y desde Primeros pasos, cuando tú quieras.',
    ],
    to: '/',
    toLabel: 'Ir a Inicio',
    keywords: ['alta', 'registro', 'onboarding', 'a medias', 'pause', 'continuar', 'incompleto', 'me sali'],
  },

  // ── Mi sitio y publicación ────────────────────────────────────────────────
  {
    id: 'publicar-cambios',
    category: 'sitio',
    question: '¿Por qué no veo mis cambios en mi sitio?',
    body: [
      'Lo que editas en el panel se guarda al momento, pero tu sitio se actualiza cuando lo publicas.',
      'Entra a Sitio y usa el botón «Publicar sitio». En un par de minutos tus visitantes ven la versión nueva; mientras tanto verás el aviso «Publicando…» en la parte de arriba del panel.',
      'Si acabas de publicar y sigues viendo lo anterior, recarga la página de tu sitio: tu navegador pudo haber guardado la versión vieja.',
    ],
    to: '/site',
    toLabel: 'Ir a Sitio',
    keywords: ['publicar', 'no se actualiza', 'cambios', 'no aparece', 'sigue igual', 'subir cambios'],
    featured: true,
  },
  {
    id: 'dominio-propio',
    category: 'sitio',
    question: 'Conectar mi propio dominio',
    body: [
      'Tu sitio nace con una dirección lista para usarse. Cuando quieras usar tu propio dominio (por ejemplo, minegocio.com), ve a Ajustes y abre la pestaña Publicación.',
      'Ahí registras el dominio que compraste y te decimos qué datos configurar con quien te lo vendió. El cambio puede tardar unas horas en verse en todo el mundo.',
      'Si no tienes dominio todavía, tu sitio funciona igual con la dirección actual: puedes compartirlo desde hoy.',
    ],
    to: '/settings?tab=publicacion',
    toLabel: 'Ir a Ajustes → Publicación',
    keywords: ['dominio', 'url', 'direccion web', 'www', 'com', 'mi nombre'],
    featured: true,
  },
  {
    id: 'apariencia',
    category: 'sitio',
    question: 'Cambiar mis colores, mi logo o la imagen principal',
    body: [
      'La apariencia de tu sitio vive en Sitio, pestaña Apariencia: los colores, las tipografías, tu logo y el ícono que aparece en la pestaña del navegador.',
      'La imagen grande de la portada se cambia desde la sección de portada de tu página, en la pestaña Páginas.',
      'Cuando termines, publica para que el cambio llegue a tu sitio.',
    ],
    to: '/site?tab=apariencia',
    toLabel: 'Ir a Apariencia',
    keywords: ['logo', 'colores', 'tipografia', 'diseño', 'imagen principal', 'portada', 'favicon', 'marca'],
  },
  {
    id: 'datos-contacto',
    category: 'sitio',
    question: 'Cambiar mi horario, mi teléfono o mi dirección',
    body: [
      'Estos datos viven en un solo lugar: Sitio, pestaña Contacto y redes. Ahí actualizas horario de atención, teléfono, WhatsApp, correo, dirección y redes sociales.',
      'Se usan en todo tu sitio a la vez —en el pie de página, en el mapa, en los botones de contacto— así que no tienes que corregirlos sección por sección.',
      'Publica al terminar para que tus clientes vean la información nueva.',
    ],
    to: '/site?tab=contacto',
    toLabel: 'Ir a Contacto',
    keywords: ['horario', 'telefono', 'whatsapp', 'direccion', 'ubicacion', 'mapa', 'redes sociales', 'correo'],
    featured: true,
  },
  {
    id: 'compartir-sitio',
    category: 'sitio',
    question: 'Compartir mi sitio y generar un código QR',
    body: [
      'En Archivos e imágenes, pestaña Códigos QR, generas un QR de tu sitio o de cualquier página para imprimirlo en tu local, en un menú o en un volante.',
      'Puedes descargarlo y usarlo cuantas veces quieras; si tu dirección cambia, el QR sigue funcionando siempre que apunte a la misma página.',
    ],
    to: '/media?tab=qr',
    toLabel: 'Ir a Códigos QR',
    keywords: ['qr', 'compartir', 'imprimir', 'volante', 'codigo'],
  },
  {
    id: 'ver-antes-de-publicar',
    category: 'sitio',
    question: 'Ver cómo va a quedar antes de publicar',
    body: [
      'Mientras editas una página, el botón con forma de ojo te muestra cómo se ve con tus cambios, aunque todavía no los hayas publicado.',
      'Puedes abrirla en otra pestaña del navegador para revisarla con calma y en el tamaño real de la pantalla.',
      'Es una vista para ti. Para que la vean tus clientes, tienes que publicar.',
    ],
    keywords: ['vista previa', 'antes de publicar', 'como queda', 'revisar', 'probar', 'ver'],
  },
  {
    id: 'pagina-enlaces',
    category: 'sitio',
    question: 'La página de enlaces para mi biografía de Instagram',
    body: [
      'Es una sola página con todos tus botones —WhatsApp, menú, cómo llegar, eventos— pensada para poner como enlace en tu perfil de redes. Hace lo mismo que servicios como Linktree, pero con tu marca y sin pagar aparte.',
      'La armas en Sitio, pestaña Enlaces: eliges los botones, su orden y puedes generar el código QR para pegarlo en tu local.',
      'Publica al terminar para que el enlace muestre lo último.',
    ],
    to: '/site?tab=enlaces',
    toLabel: 'Ir a Enlaces',
    keywords: ['enlaces', 'linktree', 'instagram', 'biografia', 'bio', 'link', 'redes', 'botones'],
  },
  {
    id: 'anuncios',
    category: 'sitio',
    question: 'Avisar de una promoción o de que cierro unos días',
    body: [
      'En Sitio, pestaña Anuncios, publicas un aviso destacado: una oferta por tiempo limitado, el cierre temporal de una sucursal o cualquier noticia importante.',
      'Puedes mostrarlo como una barra sobre el menú o como una ventana al entrar a tu sitio. Ponle fecha de caducidad y desaparece solo; también puedes pausarlo o borrarlo cuando quieras.',
      'Publica para que tus visitantes lo vean.',
    ],
    to: '/site?tab=anuncios',
    toLabel: 'Ir a Anuncios',
    keywords: ['anuncio', 'aviso', 'promocion', 'oferta', 'cerrado', 'vacaciones', 'banner', 'ventana'],
  },
  {
    id: 'segundo-idioma',
    category: 'sitio',
    question: 'Tener mi sitio también en inglés',
    body: [
      'Tu sitio está en español. Si tu público también habla inglés, en Sitio, pestaña Idiomas, activas la versión en ese idioma y tus visitantes eligen con un selector.',
      'Tener el sitio en más de un idioma es una función de los planes de pago. Al activarla, cada página puede tener su versión en inglés, y tú decides qué traduces primero.',
      'Mientras una página no tenga su versión traducida, te lo marcamos en la lista de páginas para que no se te pase.',
    ],
    to: '/site?tab=idiomas',
    toLabel: 'Ir a Idiomas',
    keywords: ['ingles', 'idioma', 'bilingue', 'traducir', 'traduccion', 'english', 'extranjeros'],
  },
  {
    id: 'avisos-legales',
    category: 'sitio',
    question: 'Mi aviso de privacidad y mis términos',
    body: [
      'En México el aviso de privacidad es obligatorio en cuanto recoges datos de tus visitantes, y tu sitio los recoge desde el momento en que alguien llena tu formulario o pide una cita.',
      'En Sitio, pestaña Avisos legales, empiezas desde una plantilla con los datos de tu negocio y la ajustas a tu caso. Ahí mismo están los términos y condiciones y el aviso de cookies.',
      'Al publicar aparecen como enlaces en el pie de página de tu sitio.',
    ],
    to: '/site?tab=legal',
    toLabel: 'Ir a Avisos legales',
    keywords: ['privacidad', 'aviso legal', 'terminos', 'condiciones', 'cookies', 'legal', 'obligatorio'],
  },

  // ── Contenido y secciones ─────────────────────────────────────────────────
  {
    id: 'editar-pagina',
    category: 'contenido',
    question: 'Cambiar los textos y las imágenes de una página',
    body: [
      'Entra a Sitio, pestaña Páginas, y abre la página que quieras. Cada bloque —portada, servicios, testimonios— se edita por separado y se ve como quedará mientras lo cambias.',
      'Puedes reordenar bloques, ocultarlos o agregar nuevos desde el mismo editor. Si te arrepientes, deshacer regresa el último cambio.',
      'Al terminar, publica para que se vea en tu sitio.',
    ],
    to: '/site?tab=paginas',
    toLabel: 'Ir a Páginas',
    keywords: ['editar', 'textos', 'imagenes', 'bloques', 'pagina', 'contenido', 'cambiar texto'],
  },
  {
    id: 'anadir-seccion',
    category: 'contenido',
    question: 'Añadir una sección nueva: menú, servicios, equipo…',
    body: [
      'En el menú lateral, abajo de tus secciones actuales, está «Añadir sección». Ahí eliges lo que necesitas —menú de platillos, servicios, equipo, catálogo, preguntas frecuentes— y queda lista para llenarse.',
      'Cada sección tiene su propia lista de registros: los capturas una vez y aparecen en tu sitio con el diseño que le corresponde.',
      'Publica cuando tengas listo el contenido para que tus visitantes lo vean.',
    ],
    keywords: ['seccion', 'menu', 'platillos', 'servicios', 'equipo', 'catalogo', 'productos', 'agregar'],
  },
  {
    id: 'subir-imagenes',
    category: 'contenido',
    question: 'Subir mis fotos y archivos',
    body: [
      'Archivos e imágenes guarda todo lo que usas en tu sitio: fotos, logos y documentos. Sube ahí lo que vayas a reutilizar y después selecciónalo desde cualquier bloque.',
      'Tus fotos se publican tal como las subes, así que conviene revisarlas antes: que se vean nítidas, pero sin que sean archivos enormes. Una foto muy pesada hace que tu sitio tarde más en abrir, y quien entra desde el celular es el primero que lo nota.',
    ],
    to: '/media',
    toLabel: 'Ir a Archivos e imágenes',
    keywords: ['fotos', 'imagenes', 'subir', 'archivos', 'galeria', 'pdf'],
  },
  {
    id: 'ia-textos',
    category: 'contenido',
    question: 'Que la inteligencia artificial me escriba los textos',
    body: [
      'En los campos de texto del editor encuentras la opción de escribir o mejorar con inteligencia artificial. Le dices de qué va tu negocio y te propone una versión; tú decides si la usas, la editas o la descartas.',
      'Nada se publica solo: los textos generados se guardan como cualquier otro cambio y llegan a tu sitio cuando publicas.',
    ],
    keywords: ['ia', 'inteligencia artificial', 'escribir', 'redactar', 'textos automaticos', 'generar'],
  },

  // ── Clientes y mensajes ───────────────────────────────────────────────────
  {
    id: 'de-donde-salen-clientes',
    category: 'clientes',
    question: '¿De dónde salen las personas que aparecen en Clientes?',
    body: [
      'En Clientes se junta toda la gente que levantó la mano desde tu sitio: quien llenó tu formulario de contacto, quien pidió una cita, quien contestó una encuesta o quien dejó sus datos en una campaña.',
      'Cada persona guarda de dónde llegó y cuándo, para que sepas qué está funcionando y a quién le toca seguimiento.',
    ],
    to: '/contacts',
    toLabel: 'Ir a Clientes',
    keywords: ['clientes', 'contactos', 'leads', 'formulario', 'mensajes', 'quien me escribio'],
  },
  {
    id: 'ver-avisos',
    category: 'clientes',
    question: '¿Dónde me entero de que alguien me escribió?',
    body: [
      'La campana de la barra superior te avisa dentro del panel: mensajes nuevos, citas pedidas y respuestas de encuestas.',
      'El detalle completo de cada persona vive en Clientes, y las citas en Reservas.',
      'Si esperabas un aviso por correo y no te llegó, revisa tu carpeta de correo no deseado antes de darlo por perdido.',
    ],
    to: '/notifications',
    toLabel: 'Ver mis avisos',
    keywords: ['avisos', 'notificaciones', 'campana', 'correo', 'no me llega', 'alertas'],
  },
  {
    id: 'importar-clientes',
    category: 'clientes',
    question: 'Traer la lista de clientes que ya tengo',
    body: [
      'Si llevas tus clientes en una hoja de cálculo, puedes subirla: aceptamos archivos de Excel y CSV, hasta cinco mil filas por archivo.',
      'Antes de guardar nada te mostramos cómo entendimos cada columna y puedes revisar fila por fila. El archivo original no se queda con nosotros.',
      'Si tu hoja es de una versión vieja de Excel, guárdala como .xlsx o como CSV y vuelve a intentarlo.',
    ],
    to: '/contacts/import',
    toLabel: 'Importar mis clientes',
    keywords: ['importar', 'excel', 'csv', 'hoja', 'lista', 'subir clientes', 'base de datos', 'contactos'],
  },
  {
    id: 'spam-formulario',
    category: 'clientes',
    question: 'Me llegan mensajes basura por el formulario',
    body: [
      'Tu formulario ya trae protecciones que trabajan sin que las veas: una trampa para los programas que llenan formularios solos y un límite de envíos seguidos desde el mismo lugar.',
      'Si aun así te sigue entrando basura, escríbenos desde esta misma ventana: podemos activarte una verificación extra que le pide al visitante confirmar que es una persona. No la encendemos sin avisar porque añade un paso más a quien sí te quiere contactar.',
      'Mientras tanto, puedes marcar esos mensajes como atendidos en Clientes para que no te estorben.',
    ],
    to: '/contacts',
    toLabel: 'Ir a Clientes',
    keywords: ['spam', 'basura', 'mensajes falsos', 'robots', 'formulario', 'publicidad'],
  },
  {
    id: 'ver-visitas',
    category: 'clientes',
    question: '¿Cuánta gente entra a mi sitio?',
    body: [
      'En Inicio, en «Estado del negocio», ves las visitas del mes, cuántas personas te escribieron por WhatsApp, cuántas hicieron algo en tu sitio y qué páginas se ven más.',
      'Los números se comparan contra el mes anterior, que es la forma útil de leerlos: importa menos el total que si va subiendo.',
      'Si todavía no aparecen esos números, escríbenos desde esta ventana y lo revisamos contigo.',
    ],
    to: '/',
    toLabel: 'Ir a Inicio',
    keywords: ['visitas', 'trafico', 'cuanta gente', 'estadisticas', 'numeros', 'metricas', 'analitica'],
  },

  // ── Reservas, encuestas y reseñas ─────────────────────────────────────────
  {
    id: 'activar-reservas',
    category: 'herramientas',
    question: 'Recibir citas o reservas desde mi sitio',
    body: [
      'En Reservas defines qué servicios ofreces, en qué horarios y cuánto dura cada cita. Con eso, tus visitantes agendan solos desde tu sitio y tú lo ves en tu agenda.',
      'Puedes bloquear días, cambiar tu disponibilidad y mandar recordatorios a quien ya agendó.',
    ],
    to: '/bookings',
    toLabel: 'Ir a Reservas',
    keywords: ['reservas', 'citas', 'agenda', 'calendario', 'horarios', 'agendar'],
  },
  {
    id: 'pedir-resenas',
    category: 'herramientas',
    question: 'Pedirle reseñas a mis clientes',
    body: [
      'En Mis reseñas mandas la petición a quien ya te compró y das seguimiento a lo que contestan. Las buenas puedes mostrarlas en tu sitio como prueba social.',
      'Si conectaste tu perfil de Google, también ves ahí las reseñas que te dejaron en Google, para tenerlo todo junto. Contestarlas se hace desde tu propia ficha de Google, no desde el panel.',
    ],
    to: '/reputation',
    toLabel: 'Ir a Mis reseñas',
    keywords: ['reseñas', 'opiniones', 'google', 'testimonios', 'calificacion', 'estrellas'],
  },
  {
    id: 'crear-encuesta',
    category: 'herramientas',
    question: 'Crear una encuesta o una campaña',
    body: [
      'En Encuestas y campañas armas un formulario para captar interés desde tus redes: preguntas, material a entregar a cambio del contacto y el enlace que compartes.',
      'Cada respuesta entra a Clientes con lo que contestó, así que sabes con quién vale la pena hablar.',
    ],
    to: '/surveys',
    toLabel: 'Ir a Encuestas y campañas',
    keywords: ['encuesta', 'campaña', 'formulario', 'instagram', 'facebook', 'captar', 'promocion'],
  },
  {
    id: 'citas-en-mi-calendario',
    category: 'herramientas',
    question: '¿Las citas se pasan solas a mi calendario?',
    body: [
      'Todavía no. Las citas que te agendan viven en Reservas, dentro del panel, y hoy no se copian a Google Calendar ni a Outlook.',
      'Para no perderte ninguna: cada cita te llega por correo cuando la agendan, y quien reservó recibe recordatorios antes de su hora. Si alguien cancela o cambia su cita desde su enlace, también te enteras.',
      'Conectar tu calendario está en nuestros planes. Si es importante para tu día a día, escríbenos: saber quién lo necesita es lo que nos dice qué construir antes.',
    ],
    to: '/bookings',
    toLabel: 'Ir a Reservas',
    keywords: ['calendario', 'google calendar', 'outlook', 'sincronizar', 'agenda', 'exportar', 'celular'],
  },
  {
    id: 'asistente-sitio',
    category: 'herramientas',
    question: '¿Qué es el Asistente que contesta en mi sitio?',
    body: [
      'Es un ayudante que aparece en tu sitio y contesta las preguntas de tus visitantes con la información de tu negocio: horarios, servicios, precios, cómo llegar. Trabaja de noche y en fin de semana, cuando tú no puedes contestar.',
      'En Asistente decides cómo se presenta y de qué habla, ves cuánto se está usando y puedes leer las conversaciones que tuvo. Ahí te enteras de lo que la gente pregunta de verdad, que casi nunca es lo que uno cree.',
      'Tiene un cupo de mensajes al día, tanto para tu negocio como para cada visitante, y el cupo depende de tu plan. Cuando se agota, en vez de dejar a la persona hablando sola, le dice que te contacte directamente y le muestra tus datos.',
    ],
    to: '/chatbot',
    toLabel: 'Ir a Asistente',
    keywords: ['asistente', 'chat', 'chatbot', 'contesta', 'preguntas', 'bot', 'automatico', 'conversaciones'],
  },
  {
    id: 'visibilidad',
    category: 'herramientas',
    question: '¿Qué es «Resumen y visibilidad»?',
    body: [
      'Es tu diagnóstico de presencia en internet: qué tan fácil es que te encuentren, qué tienes bien resuelto y qué pasos mueven más la aguja.',
      'Los pasos están ordenados por impacto: hacer los primeros suele valer más que muchos cambios pequeños.',
      'Lo que responden Google y los asistentes de inteligencia artificial se registra a mano desde esa misma pantalla: la buscas, ves qué contestó y lo anotas. El tablero avanza cuando alguien lo alimenta, no se llena solo.',
    ],
    to: '/visibility',
    toLabel: 'Ir a Resumen y visibilidad',
    keywords: ['visibilidad', 'google', 'seo', 'que me encuentren', 'buscadores', 'diagnostico'],
  },

  // ── Mi cuenta y mi pago ───────────────────────────────────────────────────
  {
    id: 'cambiar-plan',
    category: 'cuenta',
    question: 'Cambiar mi plan, ver mis recibos o cancelar',
    body: [
      'Todo lo de dinero vive en Mi pago: el estado de tu plan, tus recibos y el cambio a otro plan.',
      'Desde ahí entras al portal de pagos, donde actualizas tu tarjeta, descargas facturas de cobro y cancelas si así lo decides.',
      'Cancelar es dejar de pagar: tu sitio y tu información se conservan bajo las reglas del Plan Gratis, no se borran.',
    ],
    to: '/billing',
    toLabel: 'Ir a Mi pago',
    keywords: ['plan', 'pago', 'recibos', 'facturas', 'tarjeta', 'cancelar', 'suscripcion', 'precio'],
    featured: true,
  },
  {
    id: 'dejar-de-pagar',
    category: 'cuenta',
    question: '¿Qué pasa si dejo de pagar?',
    body: [
      'Tu sitio no desaparece. Si tu prueba termina o dejas de pagar, tu cuenta pasa al Plan Gratis: sigues entrando al panel, editando y publicando dentro de los límites de ese plan.',
      'Las funciones de los planes de pago se apagan hasta que vuelvas a activarlos, y en Mi pago te decimos exactamente qué quedó pendiente.',
      'Si tenías un cobro sin liquidar, ahí mismo encuentras cómo ponerte al corriente.',
    ],
    to: '/billing',
    toLabel: 'Ver mi pago',
    keywords: ['no pagar', 'gratis', 'free', 'vencido', 'prueba', 'se acabo', 'que pierdo'],
  },
  {
    id: 'pedir-factura',
    category: 'cuenta',
    question: 'Necesito factura de lo que pago',
    body: [
      'El comprobante de cada cobro lo descargas tú mismo desde Mi pago, en el portal de pagos: sirve para tu control y para comprobar el gasto.',
      'Si lo que necesitas es una factura fiscal a nombre de tu empresa, escríbenos desde esta misma ventana con tu RFC, tu razón social, tu uso de CFDI y el correo donde la quieres recibir. Te confirmamos en cuanto la tengamos.',
      'Pídela con los datos correctos desde el principio: corregir una factura ya emitida es más lento que emitirla bien.',
    ],
    to: '/billing',
    toLabel: 'Ir a Mi pago',
    keywords: ['factura', 'cfdi', 'rfc', 'fiscal', 'facturar', 'sat', 'comprobante', 'deducir'],
  },
  {
    id: 'invitar-equipo',
    category: 'cuenta',
    question: 'Darle acceso a alguien de mi equipo',
    body: [
      'En Ajustes, pestaña Usuarios, creas el acceso de quien quieras que te ayude y eliges qué puede hacer: editar contenido o administrar también la configuración.',
      'Nunca compartas tu contraseña: al crear el acceso le mandas una invitación o le pasas un enlace de activación para que ponga la suya.',
    ],
    to: '/settings?tab=usuarios',
    toLabel: 'Ir a Usuarios',
    keywords: ['usuarios', 'equipo', 'acceso', 'invitar', 'permisos', 'empleado', 'socio'],
    featured: true,
  },
  {
    id: 'entrar-y-contrasena',
    category: 'cuenta',
    question: 'Cambiar mi contraseña o entrar sin ella',
    body: [
      'Tu contraseña se cambia en Mi perfil, desde el menú de tu cuenta en la esquina superior derecha.',
      'Si no la recuerdas, en la pantalla de entrada puedes pedir un enlace de acceso a tu correo: te deja entrar sin contraseña y después puedes poner una nueva.',
    ],
    to: '/account',
    toLabel: 'Ir a Mi perfil',
    keywords: ['contraseña', 'password', 'entrar', 'acceso', 'olvide', 'login', 'enlace magico'],
  },
  {
    id: 'baja-de-datos',
    category: 'cuenta',
    question: 'Dar de baja mi cuenta y borrar mis datos',
    body: [
      'Cancelar el pago y borrar tus datos son dos cosas distintas: cancelar solo detiene el cobro y conserva tu sitio bajo el Plan Gratis.',
      'Si lo que quieres es que borremos tu información, escríbenos desde esta misma ventana y te decimos qué se borra, qué estamos obligados a conservar y en cuánto tiempo queda hecho.',
    ],
    keywords: ['borrar', 'eliminar cuenta', 'datos personales', 'baja', 'arco', 'privacidad'],
  },
];

export function helpArticleById(id: string): HelpArticle | undefined {
  return HELP_ARTICLES.find((a) => a.id === id);
}

export function featuredHelpArticles(): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.featured);
}

export function helpArticlesOf(category: HelpCategoryId): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.category === category);
}

/**
 * La capa de orientación (`GOL-11C`). El índice la muestra ANTES del buscador y
 * de "lo más buscado": buscar exige saber qué palabra escribir, y quien acaba de
 * entrar todavía no lo sabe. Sale de aquí y no de una lista de ids en la UI para
 * que añadir un artículo de orientación sea editar solo este archivo.
 */
export function orientationArticles(): HelpArticle[] {
  return helpArticlesOf('empezar');
}

/** Minúsculas y sin acentos: "Cómo publico" y "como publico" deben empatar. */
function normalize(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Búsqueda local del FAQ — sin red y sin IA, a propósito: es la capa 1 de D8 y
 * tiene que responder mientras el cliente escribe. Exige que TODOS los términos
 * aparezcan en algún campo, y ordena por dónde aparecen (título pesa más que
 * cuerpo) para que "dominio" no devuelva primero un artículo que solo lo menciona.
 */
export function searchHelpArticles(query: string): HelpArticle[] {
  const terms = normalize(query).split(/\s+/).filter((t) => t.length > 1);
  if (!terms.length) return [];

  const scored = HELP_ARTICLES.map((article) => {
    const question = normalize(article.question);
    const keywords = normalize(article.keywords.join(' '));
    const body = normalize(article.body.join(' '));
    let score = 0;
    for (const term of terms) {
      const hit = (question.includes(term) ? 3 : 0)
        + (keywords.includes(term) ? 2 : 0)
        + (body.includes(term) ? 1 : 0);
      if (!hit) return { article, score: 0 };
      score += hit;
    }
    return { article, score };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.article);
}

/**
 * Los artículos como contexto del asistente de ayuda. Es lo ÚNICO que el modelo
 * sabe del producto: si algo no está aquí, la respuesta correcta es escalar a
 * soporte, no inventar un procedimiento.
 */
export function helpArticlesAsContext(): string {
  return HELP_ARTICLES.map((a) => [
    `## [${a.id}] ${a.question}`,
    a.to ? `Pantalla: ${a.to}${a.toLabel ? ` (${a.toLabel})` : ''}` : null,
    ...a.body,
  ].filter(Boolean).join('\n')).join('\n\n');
}
